import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { Odpoved, DisplejStylOdpovedi } from '../api/JsdhNotifikaceService/generated';
import OdpovedKarta from './OdpovedKarta';
import { useNestihaVyjezd } from '../hooks/useNestihaVyjezd';

interface OdpovediHasicuProps {
    odpovedi: Odpoved[];
    test?: boolean;
    showSpecializace?: boolean;
    displejStyl?: DisplejStylOdpovedi;
    /** ISO cas vyhlaseni poplachu — predava se kartam kvuli odpoctu dojezdu. */
    casVyhlaseni?: string;
    /** @deprecated Po redesignu (2026-04-29) se ignoruje — layout je vždy 2-col split podle displejStyl. Prop zůstává pro zpětnou kompatibilitu volajících. */
    adaptivniLayout?: boolean;
}

const generateTestOdpovedi = (): Odpoved[] => {
    const jmena = [
        'Jan Novák', 'Petr Svoboda', 'Martin Dvořák', 'Tomáš Černý', 'Josef Procházka',
        'Pavel Kučera', 'Jaroslav Veselý', 'Miroslav Horák', 'Zdeněk Němec', 'František Marek',
        'Václav Pospíšil', 'Karel Hájek', 'Milan Jelínek', 'Jiří Král', 'Lukáš Růžička',
        'David Beneš', 'Jakub Fiala', 'Ondřej Sedláček', 'Radek Doležal', 'Michal Urban',
        'Adam Kovář', 'Marek Kratochvíl', 'Stanislav Blažek', 'Roman Kolář', 'Filip Navrátil',
        'Vojtěch Zeman', 'Aleš Štěpánek', 'Daniel Holub', 'Patrik Musil', 'Matěj Pokorný'
    ];
    const now = new Date().toISOString();
    return jmena.map((jmeno, index): Odpoved => {
        const isJedu = index < 20;
        const funkceKod = index < 2 ? 'VD' : index < 6 ? 'S' : 'H';
        const funkceNazev = funkceKod === 'VD' ? 'Velitel družstva' : funkceKod === 'S' ? 'Strojník' : 'Hasič';
        const specializace = index === 0 ? [{ id: 1, kod: 'NDT', nazev: 'Nositel dýchací techniky' }]
            : index === 2 ? [{ id: 2, kod: 'PILAR', nazev: 'Obsluhovatel motorové pily' }]
            : index === 3 ? [{ id: 1, kod: 'NDT', nazev: 'Nositel dýchací techniky' }, { id: 2, kod: 'PILAR', nazev: 'Obsluhovatel motorové pily' }]
            : [];
        return {
            id: index + 1,
            poplachId: 1,
            uzivatelId: index + 1,
            jmeno,
            odpoved: isJedu ? 'JEDU' : 'NEJEDU',
            cas: now,
            funkce: [{ id: index + 1, kod: funkceKod, nazev: funkceNazev }],
            funkceAsString: funkceNazev,
            specializace,
        };
    });
};

const counterColor = (jedu: boolean): string => (jedu ? '#2e7d32' : '#c62828');

const CounterChip: React.FC<{ jedu: boolean; count: number }> = ({ jedu, count }) => (
    <Box
        data-counter={jedu ? 'JEDU' : 'NEJEDU'}
        sx={{
            backgroundColor: counterColor(jedu),
            color: '#fff',
            borderRadius: 'var(--dashboard-counter-radius, 8px)',
            padding: 'var(--dashboard-counter-padding, 12px 8px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            minWidth: 'var(--dashboard-counter-width, 96px)',
            width: 'var(--dashboard-counter-width, 96px)',
            alignSelf: 'stretch',
        }}
    >
        <Box
            component="span"
            sx={{
                fontSize: 'var(--dashboard-counter-label-size, 1rem)',
                letterSpacing: '1px',
                opacity: 0.95,
                lineHeight: 1,
            }}
        >
            {jedu ? 'JEDU' : 'NEJEDU'}
        </Box>
        <Box
            component="span"
            sx={{
                fontSize: 'var(--dashboard-counter-num-size, 3.5rem)',
                lineHeight: 1,
                marginTop: 0.5,
                fontVariantNumeric: 'tabular-nums',
            }}
        >
            {count}
        </Box>
    </Box>
);

interface SectionWithCounterProps {
    jedu: boolean;
    odpovedi: Odpoved[];
    showSpecializace: boolean;
    casVyhlaseni?: string;
    jeNestihajici: (o: Odpoved) => boolean;
}

const SectionWithCounter: React.FC<SectionWithCounterProps> = ({
    jedu, odpovedi, showSpecializace, casVyhlaseni, jeNestihajici,
}) => {
    const counter = <CounterChip jedu={jedu} count={odpovedi.length} />;
    const grid = (
        <Box
            data-section={jedu ? 'JEDU' : 'NEJEDU'}
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 0.75,
                alignContent: 'start',
                flex: 1,
                minWidth: 0,
            }}
        >
            {odpovedi.map((o) => (
                <OdpovedKarta
                    key={o.id ?? o.uzivatelId}
                    odpoved={o}
                    showSpecializace={showSpecializace}
                    displayStyle="BACKGROUND_TINTED"
                    tintCard={true}
                    largeMode={false}
                    casVyhlaseni={casVyhlaseni}
                    nestiha={jeNestihajici(o)}
                />
            ))}
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', minWidth: 0 }}>
            {jedu ? counter : grid}
            {jedu ? grid : counter}
        </Box>
    );
};

interface StripeSectionProps {
    jedu: boolean;
    odpovedi: Odpoved[];
    showSpecializace: boolean;
    casVyhlaseni?: string;
    jeNestihajici: (o: Odpoved) => boolean;
}

const StripeSection: React.FC<StripeSectionProps> = ({ jedu, odpovedi, showSpecializace, casVyhlaseni, jeNestihajici }) => (
    <Box
        data-section={jedu ? 'JEDU' : 'NEJEDU'}
        sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 0.5,
            alignContent: 'start',
            minWidth: 0,
        }}
    >
        {odpovedi.map((o) => (
            <OdpovedKarta
                key={o.id ?? o.uzivatelId}
                odpoved={o}
                showSpecializace={showSpecializace}
                displayStyle="STRIPE"
                largeMode={false}
                casVyhlaseni={casVyhlaseni}
                nestiha={jeNestihajici(o)}
            />
        ))}
    </Box>
);

const OdpovediHasicu: React.FC<OdpovediHasicuProps> = ({
    odpovedi,
    test = false,
    showSpecializace = false,
    displejStyl = 'STRIPE',
    casVyhlaseni,
}) => {
    const data = useMemo(() => (test ? generateTestOdpovedi() : odpovedi), [test, odpovedi]);
    // Jeden observer na celou obrazovku, ne na kazdou kartu (RPi).
    const jeNestihajici = useNestihaVyjezd(casVyhlaseni, data);
    const jedouOdpovedi = useMemo(() => data.filter((o) => o.odpoved === 'JEDU'), [data]);
    const nejedouOdpovedi = useMemo(() => data.filter((o) => o.odpoved === 'NEJEDU'), [data]);

    if (displejStyl === 'BACKGROUND_TINTED') {
        return (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1,
                    pt: 1,
                }}
            >
                <SectionWithCounter
                    jedu={true}
                    odpovedi={jedouOdpovedi}
                    showSpecializace={showSpecializace}
                    casVyhlaseni={casVyhlaseni}
                    jeNestihajici={jeNestihajici}
                />
                <SectionWithCounter
                    jedu={false}
                    odpovedi={nejedouOdpovedi}
                    showSpecializace={showSpecializace}
                    casVyhlaseni={casVyhlaseni}
                    jeNestihajici={jeNestihajici}
                />
            </Box>
        );
    }

    // STRIPE styl - 2-col split JEDU/NEJEDU bez counter chipu (kompaktni karty s vlastnim JEDU/NEJEDU chipem)
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
                pt: 1,
            }}
        >
            <StripeSection jedu={true} odpovedi={jedouOdpovedi} showSpecializace={showSpecializace} casVyhlaseni={casVyhlaseni} jeNestihajici={jeNestihajici} />
            <StripeSection jedu={false} odpovedi={nejedouOdpovedi} showSpecializace={showSpecializace} casVyhlaseni={casVyhlaseni} jeNestihajici={jeNestihajici} />
        </Box>
    );
};

export default React.memo(OdpovediHasicu);
